import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { LuMailCheck, LuArrowLeft } from "react-icons/lu";
import { Brand, Field, Note } from "../components/Ui";
import SEO from "../components/SEO";
import "./auth.css";

const ENDPOINT = "https://us-east1-foursssolutions.cloudfunctions.net/send_activation_link_v2";

export default function ForgotPassword() {
  const resetEmail = useSelector((s) => s.profile.resetEmail);
  const navigate = useNavigate();

  const [userEmail, setUserEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");

  /* prefill from redux resetEmail on mount (same as legacy) */
  useEffect(() => {
    setUserEmail(resetEmail || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* once the link is sent, show the confirmation briefly then return to sign in */
  useEffect(() => {
    if (!sentTo) return undefined;
    const t = setTimeout(() => navigate("/login"), 4000);
    return () => clearTimeout(t);
  }, [sentTo, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSentTo("");

    /* same endpoint + payload as legacy */
    const payload = {
      task: "send_passwordresetlink",
      user_mail: userEmail,
    };

    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (response.ok) {
        setSentTo(userEmail);
      } else {
        setError(result.error || "Failed to send reset link. Please try again.");
      }
    } catch (err) {
      setError("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="csx">
      <SEO
        title="Reset your password"
        description="Request a password reset link for your CareerSavvy account."
        canonicalPath="/page-forgot-password"
        noIndex
      />
      <div className="csx-page">
        <main className="auth-hero">
          <div className="auth-mesh" aria-hidden="true" />
          <div className="auth-box">
            <div className="auth-brand">
              <Brand />
            </div>

            <div className="ld-glass auth-panel stack-3">
              <div className="stack-2">
                <h1 className="auth-title">Forgot your password?</h1>
                <p className="t-sm">Enter your email and we'll send you a link to reset it.</p>
              </div>

              {sentTo ? (
                <Note tone="accent">
                  <LuMailCheck size={17} />
                  <span>
                    Reset link sent to <strong>{sentTo}</strong>. Check your inbox — taking you back to sign in…
                  </span>
                </Note>
              ) : null}
              {error ? (
                <Note tone="danger">
                  <span>{error}</span>
                </Note>
              ) : null}

              {!sentTo ? (
                <form className="stack-3" onSubmit={handleSubmit} noValidate>
                  <Field label="Email">
                    <input
                      className="input"
                      type="email"
                      autoComplete="username"
                      placeholder="you@example.com"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                    />
                  </Field>
                  <button type="submit" className="btn btn-mint btn-lg btn-full" disabled={loading}>
                    {loading ? <span className="spinner" /> : null}
                    Send reset link
                  </button>
                </form>
              ) : (
                <Link to="/login" className="btn btn-mint btn-lg btn-full">
                  Back to sign in now
                </Link>
              )}

              <p className="t-sm t-center">
                Remembered your password?{" "}
                <Link to="/login" className="t-accent" style={{ fontWeight: 600 }}>
                  Sign in
                </Link>
              </p>
              <p className="t-sm t-center">
                New to CareerSavvy?{" "}
                <Link to="/page-register" className="t-accent" style={{ fontWeight: 600 }}>
                  Create a free account
                </Link>
              </p>
            </div>

            <p className="t-sm t-center" style={{ marginTop: 6 }}>
              <Link
                to="/"
                style={{ display: "inline-flex", alignItems: "center", gap: 6, opacity: 0.75, fontWeight: 600 }}
              >
                <LuArrowLeft size={15} /> Back to home
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
